import { DatePicker, Form, Radio } from "antd";
import dayjs from "dayjs";
import { ReactNode, useEffect, useState } from "react";
import DateIcon from "../../assets/icons/DateIcon";
import { dateFormat, dbDateFormat } from "../../constants/constants";
import useModal from "../../hooks/useModal";
import { disableFutureDates, formatDate } from "../../lib/time";
import { toast } from "../../lib/toast";
import { CustomModalProps } from "../../types";
import ConfirmModal from "../ui/ConfirmModal";
import CustomModal from "../ui/CustomModal";
import ColumnListModal from "./ColumnListModal";
import SuccessModal from "./SuccessModal";

type SelectedReportType = "30" | "180" | "CUSTOM";

export type DateRangeType = {
  startDate: string;
  endDate: string;
};

const defaultDateRange = {
  startDate: new Date(
    new Date().setDate(new Date().getDate() - 30)
  ).toISOString(),
  endDate: new Date().toISOString(),
};

type ReportRequestModalType = {
  handleSubmit: (
    params: { start_date: string; end_date: string },
    columnList?: string[]
  ) => void;
  isLoading?: boolean;
  isSuccess?: boolean;
  disabled?: boolean;
  confirmMessage?: ReactNode;
  columnList?: string[];
} & CustomModalProps;

type ModalType = "CONFIRM" | "SUCCESS" | "COLUMN_LIST";

export default function ReportRequestModal({
  handleSubmit,
  isLoading,
  isSuccess,
  disabled,
  confirmMessage,
  columnList,
  ...rest
}: ReportRequestModalType) {
  const [form] = Form.useForm();
  const [reportType, setReportType] = useState<SelectedReportType | undefined>(
    disabled ? undefined : "30"
  );
  const [dateRange, setDateRange] = useState<DateRangeType>(defaultDateRange);
  const { open, handleClose, handleOpen } = useModal<string[], ModalType>();

  const handle30Days = () => {
    if (disabled) return;

    setReportType("30");
    setDateRange(defaultDateRange);
  };

  const handle180Days = () => {
    if (disabled) return;

    setReportType("180");
    setDateRange({
      startDate: new Date(
        new Date().setDate(new Date().getDate() - 180)
      ).toISOString(),
      endDate: new Date().toISOString(),
    });
  };

  const handleCustomRange = () => {
    if (disabled) return;

    setReportType("CUSTOM");
    setDateRange({
      startDate: "",
      endDate: "",
    });
  };

  const handleOk = () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      toast("error", "Please select a date range.");
      return;
    }

    if (columnList?.length) {
      handleOpen({ type: "COLUMN_LIST" });
    } else {
      handleOpen({ type: "CONFIRM" });
    }
  };

  useEffect(() => {
    if (isSuccess) {
      handleOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <CustomModal
      title="Report Requests"
      okText="Confirm"
      okButtonProps={{
        className: "!rounded !min-w-[110px]",
      }}
      cancelButtonProps={{
        className: "cancel-btn !min-w-[110px]",
      }}
      onOk={handleOk}
      width={670}
      {...rest}
    >
      {open?.type === "CONFIRM" && (
        <ConfirmModal
          open={open?.open}
          handleOk={() =>
            handleSubmit(
              {
                start_date: dayjs(dateRange?.startDate).format(dbDateFormat),
                end_date: dayjs(dateRange?.endDate).format(dbDateFormat),
              },
              open?.data
            )
          }
          handleClose={handleClose}
          title="Confirm Request"
          hideAlert
          okText="Confirm"
          okButtonProps={{
            className: "!rounded !min-w-[110px]",
            loading: isLoading,
          }}
          cancelButtonProps={{
            className: "cancel-btn !min-w-[110px]",
          }}
          // modalClassName="ff"
        >
          <div className="self-stretch justify-start text-slate-600 text-sm font-normal mb-3">
            {disabled && confirmMessage
              ? confirmMessage
              : `You are requesting a Report Export range from ${formatDate(
                  dateRange?.startDate,
                  "DD/MM/YYYY"
                )} to ${formatDate(dateRange?.endDate, "DD/MM/YYYY")}.`}
          </div>
          <div className="self-stretch justify-start text-slate-600 text-sm font-normal">
            Are you sure you want to submit this request?
          </div>
        </ConfirmModal>
      )}

      {open?.type === "COLUMN_LIST" && (
        <ColumnListModal
          open={open?.open}
          columnList={columnList!}
          handleClose={handleClose}
          handleSubmit={(selected) => {
            handleOpen({
              type: "CONFIRM",
              data: selected,
            });
          }}
        />
      )}

      {open?.type === "SUCCESS" && (
        <SuccessModal open={open?.open} handleClose={handleClose} />
      )}

      <div className="flex items-center flex-wrap gap-3">
        {reportTypes.map((type) => (
          <label
            key={type.value}
            className={`flex-1 flex items-center mb-2 custom-radio border border-gray px-2 py-1 min-h-[50px] rounded-lg ${
              disabled ? "cursor-default bg-light/50" : "cursor-pointer"
            }`}
            htmlFor={type.value}
          >
            <Radio
              id={type.value}
              name="reportType"
              value={type.value}
              checked={reportType === type.value}
              onChange={() => {
                if (type.value === "30") {
                  handle30Days();
                } else if (type.value === "180") {
                  handle180Days();
                } else if (type.value === "CUSTOM") {
                  handleCustomRange();
                }
              }}
              className="mr-2"
            />
            <div>
              <h6 className="text-body text-sm font-medium">{type.label}</h6>
              {(reportType === "30" || reportType === "180") &&
              reportType === type.value ? (
                <span className="text-slate-500 !text-[10px] !leading-[0px] mt-1">
                  {`From ${formatDate(
                    dateRange?.startDate,
                    "DD/MM/YYYY"
                  )} to ${formatDate(dateRange?.endDate, "DD/MM/YYYY")}`}
                </span>
              ) : null}
            </div>
          </label>
        ))}
      </div>

      {reportType === "CUSTOM" && (
        <Form
          className="custom-form mt-2"
          layout="vertical"
          requiredMark="optional"
          size="large"
          form={form}
        >
          <div className="grid md:grid-cols-2 gap-x-4">
            <Form.Item name="From" label="From Date" className="mb-1">
              <DatePicker
                format={dateFormat}
                suffixIcon={<DateIcon width={18} height={20} />}
                style={{ width: "100%" }}
                size="large"
                value={dateRange.startDate ? dayjs(dateRange.startDate) : null}
                onChange={(date) => {
                  form.setFieldsValue({ To: undefined });
                  setDateRange({
                    startDate: date?.toISOString() ?? "",
                    endDate: "",
                  });
                }}
                disabledDate={disableFutureDates}
              />
            </Form.Item>
            <Form.Item name="To" label="To Date" className="mb-1">
              <DatePicker
                format={dateFormat}
                suffixIcon={<DateIcon width={18} height={20} />}
                style={{ width: "100%" }}
                size="large"
                disabled={!dateRange?.startDate}
                value={dateRange.endDate ? dayjs(dateRange.endDate) : null}
                onChange={(date) => {
                  setDateRange((prev) => ({
                    startDate: prev.startDate,
                    endDate: date?.toISOString() ?? "",
                  }));
                }}
                disabledDate={(current) => {
                  const fromDate = dateRange.startDate
                    ? dayjs(dateRange.startDate)
                    : null;
                  return (
                    !current ||
                    current > dayjs().endOf("day") ||
                    (fromDate ? current < fromDate.startOf("day") : false)
                  );
                }}
              />
            </Form.Item>
          </div>
        </Form>
      )}
      <div className="p-4 mt-4 bg-amber-100 rounded text-[#664D03]">
        You can request a report 2 times a day, and total 5 times a month,
        Maximum time range for a Report is 12 months, Processing time may take
        up to 24 hours.
      </div>
    </CustomModal>
  );
}

const reportTypes = [
  { label: "Last 30 Days", value: "30" },
  { label: "Last 180 Days", value: "180" },
  { label: "Customized Range", value: "CUSTOM" },
];
